import type { DifficultyId } from '@/config/gameplay';

export interface Vec2 {
  x: number;
  z: number;
}

/** Axis-aligned or rotated box on the XZ plane with a visual height. */
export interface Collider {
  id: string;
  cx: number;
  cz: number;
  hw: number;
  hd: number;
  rot: number;
  height: number;
  /** Colliders that block movement but not bullets/vision (e.g. low fences) set this. */
  lowObstacle?: boolean;
  /** Waist-high colliders the player can vault across. */
  vaultable?: boolean;
  /** Doors can be opened and stop blocking. */
  doorId?: string;
}

/**
 * Enemy states. `wander`, `investigate` and `chase` are locomotion states where the crowd agent
 * owns the position; every other state freezes the agent on the body so nothing can snap.
 */
export type ThreatAiState = 'idle' | 'wander' | 'investigate' | 'chase' | 'attack' | 'hitReact' | 'stagger' | 'knockdown' | 'dead';

export const LOCOMOTION_STATES: ReadonlySet<ThreatAiState> = new Set<ThreatAiState>(['wander', 'investigate', 'chase']);

/** What a hit did to an enemy (drives animation and audio). */
export type HitReaction = 'hitReact' | 'stagger' | 'knockdown' | 'dead';

export interface ThreatSaveState {
  x: number;
  z: number;
  yaw: number;
  health: number;
  alive: boolean;
}

export type EquippedItem = 'pistol' | 'medkit';
export const EQUIPPABLE: readonly EquippedItem[] = ['pistol', 'medkit'];
export type DoorState = 'closed' | 'open';

export interface PlayerSaveState {
  x: number;
  z: number;
  yaw: number;
  health: number;
  stamina: number;
  ammoLoaded: number;
  ammoReserve: number;
  medkits: number;
  hasFlashlight: boolean;
  flashlightOn: boolean;
  equipped: EquippedItem;
  /** Registry items without a dedicated field (dressings, antiseptic, keys). Absent in v1 saves. */
  items?: Record<string, number>;
}

export interface LookSaveState {
  yaw: number;
  pitch: number;
}

/** Everything needed to restore a run. Transient AI timers and animation state are excluded. */
export interface RunState {
  version: number;
  seed: number;
  difficulty: DifficultyId;
  playtimeSec: number;
  checkpointId: string;
  objectiveIndex: number;
  player: PlayerSaveState;
  look: LookSaveState;
  threats: Record<string, ThreatSaveState>;
  pickupsTaken: Record<string, boolean>;
  doors: Record<string, DoorState>;
  flags: Record<string, boolean>;
  documentsRead: string[];
  completed: boolean;
}

export const RUN_STATE_VERSION = 1;

export type NoiseKind = 'footstep' | 'sprint' | 'gunshot' | 'door' | 'impact' | 'land';

export interface NoiseEvent {
  x: number;
  z: number;
  radius: number;
  kind: NoiseKind;
}
